import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Box, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSigningFormat } from "@/components/signing/format";
import { SigningRequestStatus } from "@/lib/signing/types";
/** A ballot box's status, as the Post's Dashboard words it. */
var ballotBoxStatus = function (t, box, time) {
    switch (box.status) {
        case "pending":
            // Past its deadline, the sealer takes it on its next run.
            return new Date(box.grace_deadline).getTime() <= Date.now()
                ? t("dashboard.ballotBoxes.status.due")
                : t("dashboard.ballotBoxes.status.sealing", { time: time(box.grace_deadline) });
        case "sealed":
            return t("dashboard.ballotBoxes.status.publishing");
        case "published":
            return t("dashboard.ballotBoxes.status.sealed");
        default:
            return t("dashboard.ballotBoxes.status.failed");
    }
};
var shortHash = function (hash) {
    return hash.length > 16 ? "".concat(hash.slice(0, 8), "\u2026").concat(hash.slice(-8)) : hash;
};
/** A table of label and value rows. */
var RowsTable = function (_a) {
    var label = _a.label, rows = _a.rows;
    return (_jsx(Table, { size: "small", "aria-label": label, children: _jsx(TableBody, { children: rows.map(function (_a) {
                var name = _a[0], value = _a[1];
                return (_jsxs(TableRow, { children: [_jsx(TableCell, { component: "th", scope: "row", sx: { width: "45%", color: "text.secondary" }, children: name }), _jsx(TableCell, { sx: { overflowWrap: "anywhere" }, children: value })] }, name));
            }) }) }));
};
/**
 * The closed-voting card of a completed close voting request: when voting
 * closed, one block per country ballot box (as it stands now, or as the
 * stored result shows it), then the closing signatures and the signing code.
 */
export var ClosedVotingCard = function (_a) {
    var _b, _c, _d, _e, _f;
    var data = _a.data;
    var t = useTranslation().t;
    var format = useSigningFormat(data.time_zone);
    var record = ((_b = data.request.execution_result) !== null && _b !== void 0 ? _b : null);
    if (data.request.status !== SigningRequestStatus.Executed || !(record === null || record === void 0 ? void 0 : record.closed_at)) {
        return (_jsx(Alert, { severity: "info", sx: { width: "100%" }, "data-testid": "closed-voting-pending", children: t("signing.closed.pending") }));
    }
    var signatures = (_c = record.signatures) !== null && _c !== void 0 ? _c : [];
    var seals = (_d = record.seals) !== null && _d !== void 0 ? _d : [];
    // The boxes are sealed after the close (VOTE-FREEZE), so the panel's
    // current ballot boxes, when it has them, stand in for the stored seals.
    var ballotBoxes = (_e = data.ballot_boxes) !== null && _e !== void 0 ? _e : [];
    var boxBlocks = ballotBoxes.map(function (box) {
        var rows = [
            [t("dashboard.ballotBoxes.column.status"), ballotBoxStatus(t, box, format.time)],
        ];
        if (box.ballots !== undefined && box.ballots !== null) {
            rows.push([t("signing.closed.ballots"), box.ballots.toLocaleString()]);
        }
        if (box.seal_hash) {
            rows.push([
                t("signing.closed.sealHash", { algorithm: box.hash_algorithm }),
                shortHash(box.seal_hash),
            ]);
        }
        return { key: box.area_id, country: box.area_name || box.area_id, rows: rows };
    });
    var allSealed = ballotBoxes.length > 0
        ? ballotBoxes.every(function (box) { return box.status === "sealed" || box.status === "published"; })
        : seals.length > 0;
    var storedBlocks = seals.map(function (seal, index) {
        var _a, _b;
        var rows = [];
        if (seal.ballots !== undefined && seal.ballots !== null) {
            rows.push([t("signing.closed.ballots"), seal.ballots.toLocaleString()]);
        }
        if (seal.hash) {
            rows.push([
                t("signing.closed.sealHash", { algorithm: (_a = seal.hash_algorithm) !== null && _a !== void 0 ? _a : "" }),
                shortHash(seal.hash),
            ]);
        }
        if (seal.signed_by) {
            rows.push([t("signing.closed.signedBy"), seal.signed_by]);
        }
        var country = seal.area_name || seal.area_id || "";
        return { key: "".concat((_b = seal.area_id) !== null && _b !== void 0 ? _b : "", "-").concat(index), country: country, rows: rows };
    });
    var sealBlocks = ballotBoxes.length > 0 ? boxBlocks : storedBlocks;
    var rows = [];
    rows.push([
        t("signing.closed.signatures"),
        t("signing.closed.signaturesValue", {
            count: signatures.length,
            code: (_f = record.code) !== null && _f !== void 0 ? _f : data.request.code,
        }),
    ]);
    rows.push([
        t("signing.closed.signers"),
        signatures
            .map(function (signature) { var _a, _b; return (_b = (_a = signature.display_name) !== null && _a !== void 0 ? _a : signature.username) !== null && _b !== void 0 ? _b : ""; })
            .filter(Boolean)
            .join(", "),
    ]);
    return (_jsxs(Box, { "data-testid": "closed-voting", sx: {
            width: "100%",
            border: 1,
            borderColor: "success.light",
            borderRadius: 1,
            bgcolor: "rgba(46, 125, 50, 0.06)",
            p: 2,
        }, children: [_jsx(Typography, { component: "h3", sx: { fontWeight: 600, mb: 1 }, children: allSealed
                    ? t("signing.closed.titleSealed", { time: format.time(record.closed_at) })
                    : t("signing.closed.title", { time: format.time(record.closed_at) }) }), sealBlocks.map(function (_a) {
                var key = _a.key, country = _a.country, sealRows = _a.rows;
                return (_jsxs(Box, { sx: { mb: 1 }, children: [_jsx(Typography, { component: "h4", variant: "body2", sx: { fontWeight: 600 }, children: country }), _jsx(RowsTable, { label: country, rows: sealRows })] }, key));
            }), _jsx(RowsTable, { label: t("signing.closed.record"), rows: rows })] }));
};
